import * as R from 'ramda';
import {
  ACCESS_RESOURCES_TO_HUMAN_READABLE,
  AD_NETWORKS,
  AGENCY_DATA_FILTERS,
  AGENCY_ROLE_IDS,
  AGENCY_TAGGED_DATA,
  AGGREGATE_DATA,
  ALL_AGENCY_DATA_FILTERS_COPY,
  APP_LEVEL_SETTINGS,
  AUDIENCE_BUILDER,
  CHANNEL_LEVEL_SETTINGS,
  EXPORT_ACCESS,
  FRAUD_MANAGER,
  FRAUD_REPORTING,
  GREATEST_PREDEFINED_ROLE_ID,
  LINK_LEVEL_SETTINGS,
  LINK_TEMPLATE_SETTINGS,
  LOCATION,
  MOBILE_SEARCH,
  REVENUE_DATA,
  ROLE_IDS,
  ROLE_IDS_TO_HUMAN_READABLE,
  SENSITIVE_DATA,
  UI_FEATURE_RESTRICTION_OPTIONS,
} from '../accessControlConstants';
import { createTextListFromArray } from './index';

import type {
  AccessFeatureFilters,
  AccessResource,
  AccessResourcePermissions,
  FilterToggles,
  Filters,
  Permissions,
  UiRoleId,
  VendorAccessResource,
} from '../../../main/types/accessControlTypes';

const isAgencyRole = (roleId?: number) => {
  return roleId && Object.values(AGENCY_ROLE_IDS).includes(roleId as UiRoleId);
};

export const getResourceText = (accessResources: AccessResource[]) => {
  if (!accessResources || !accessResources.length) {
    return '';
  }

  // INTENG-8423 Display all agency data filters in ForbiddenView screen as 'with no data filters' instead of individually
  // i.e. sensitive data and export permission, with no data filters instead of
  //      sensitive data, export permissions, agency tagged data, revenue data, all ad networks, and all locations
  if (AGENCY_DATA_FILTERS.every((v) => accessResources.includes(v))) {
    const resourcesToConvert = accessResources.filter((v) => !AGENCY_DATA_FILTERS.includes(v as VendorAccessResource));
    const readableResources = resourcesToConvert.map((resource) => ACCESS_RESOURCES_TO_HUMAN_READABLE[resource]);
    return createTextListFromArray(readableResources || []) + ALL_AGENCY_DATA_FILTERS_COPY;
  }

  const readableResources = accessResources.map((resource) => ACCESS_RESOURCES_TO_HUMAN_READABLE[resource]);
  return createTextListFromArray(readableResources || []);
};

export const hasAccess = function (
  accessResources: string[],
  requiredPermission: number,
  userPermissions?: AccessResourcePermissions | null
) {
  if (!accessResources || !userPermissions) {
    return false;
  }

  if (requiredPermission === undefined) {
    return false;
  }

  return accessResources.every((resource) => {
    const accessResource = userPermissions && userPermissions[resource];
    const userPermission = accessResource && accessResource.permission;
    if (userPermission === undefined || userPermission === null) {
      return false;
    }

    return requiredPermission <= userPermission;
  });
};

// For Team Add/Edit Modals:
export const getPermissionsFromRoleId = (uiRoleId: UiRoleId, props: Record<string, any>) => {
  const result = {
    permissions: {
      [LINK_LEVEL_SETTINGS]: 0,
      [CHANNEL_LEVEL_SETTINGS]: 0,
      [LINK_TEMPLATE_SETTINGS]: 0,
      [APP_LEVEL_SETTINGS]: 0,
      [AGGREGATE_DATA]: 0,
      [SENSITIVE_DATA]: 0,
      [EXPORT_ACCESS]: 0,
      [FRAUD_REPORTING]: 0,
      [FRAUD_MANAGER]: 0,
      [AUDIENCE_BUILDER]: 0,
      [MOBILE_SEARCH]: 0,
    },
    featureFilters: {},
  };

  const defaultRoles = props?.accessControl?.defaultRoles || {};
  const defaultRoleFilters = props?.accessControl?.defaultRoleFilters || {};

  // if custom, see if permissions is saved by userId
  if (!(uiRoleId in defaultRoles) && !isAgencyRole(uiRoleId)) {
    const userId = R.path<string | undefined>(['match', 'params', 'userId'], props);
    return {
      ...R.pathOr(result, ['team', 'permissionsByUserId', userId || ''], props),
    };
  }

  if (uiRoleId in defaultRoles) {
    return {
      permissions: defaultRoles[uiRoleId],
      featureFilters: defaultRoleFilters[uiRoleId] || {},
    };
  }

  return result;
};

/**
 * converts an accessRoleId to a uiRoleId
 * @param  {string | undefined} accessRoleId What the database stores as the accessRoleId
 * @param  {* | undefined} appDefaultRoles
 * '1', '2', '3', and '4' represent the standard roles, anything above is custom
 * @return {number} A number 1 - 5 representing the 5 choices in the dropdown
 * Input: '1'
 * Output: 1
 *
 * Input: '123'
 * Output: 5
 */
export const convertAccesstoUIRoleId = (accessRoleId?: string | null, appDefaultRoles?: any): UiRoleId | string => {
  if (accessRoleId && appDefaultRoles && appDefaultRoles[accessRoleId]?.length) {
    return accessRoleId;
  }

  const accessRoleIdNumber = Number(accessRoleId);
  if (!accessRoleIdNumber) {
    return ROLE_IDS.TEAM_MEMBER;
  }
  if (isAgencyRole(accessRoleIdNumber)) {
    return (
      R.find<UiRoleId | string>((roleId) => roleId === accessRoleIdNumber)(Object.values(AGENCY_ROLE_IDS)) ||
      AGENCY_ROLE_IDS.TEAM_MEMBER
    );
  }
  if (accessRoleIdNumber > GREATEST_PREDEFINED_ROLE_ID) {
    return ROLE_IDS.CUSTOM;
  }
  return (
    R.find<UiRoleId | string>((roleId) => roleId === accessRoleIdNumber)(Object.values(ROLE_IDS)) || ROLE_IDS.TEAM_MEMBER
  );
};

export const getRoleFromAccessId = (accessRoleId?: string | null, appDefaultRoles?: any): string => {
  if (accessRoleId && appDefaultRoles && accessRoleId in appDefaultRoles) {
    return appDefaultRoles[accessRoleId] || 'Custom';
  }

  const uiRoleId = convertAccesstoUIRoleId(accessRoleId);
  return ROLE_IDS_TO_HUMAN_READABLE[uiRoleId];
};

export const isFormFilled = (
  isExistingUser: boolean,
  {
    firstName,
    lastName,
    email,
    permissions = {},
  }: { firstName?: string; lastName?: string; email?: string; permissions: Record<string, any> }
) => {
  // existing names may not have first or last (or either) names filled in,
  // and we don't want to prevent inviting or saving such a user
  const namesFilled = isExistingUser ? true : !!firstName && !!lastName;
  const inputsFilled = namesFilled && !!email;
  const permissionSelected = !!permissions
    ? !R.all((key: string) => permissions[key] === 0)(Object.keys(permissions))
    : false;
  return inputsFilled && permissionSelected;
};

export const simplifyPermissions = (permissionsByResource: AccessResourcePermissions): Permissions | {} | null => {
  const resources = Object.keys(permissionsByResource);
  if (!resources || !resources.length) {
    return null;
  }
  return resources.reduce((simplePermissions, resource) => {
    const newPermissions = { ...simplePermissions };
    newPermissions[resource] = R.path([resource, 'permission'], permissionsByResource);
    return newPermissions;
  }, {});
};

export const determineFilters = (permissionsByResource: AccessResourcePermissions): Filters => ({
  ad_networks: R.path([AD_NETWORKS, 'access_filter'], permissionsByResource),
  location: R.path([LOCATION, 'access_filter'], permissionsByResource),
});

export const resetFilterToggles: () => FilterToggles = () => {
  return {
    [AGENCY_TAGGED_DATA]: false,
    [AD_NETWORKS]: false,
    [LOCATION]: false,
    [REVENUE_DATA]: false,
  };
};

export const resetFilters = () => {
  return {
    ad_networks: [],
    location: [],
  };
};

export const extractFeatureFilters = (accessResourcePermissions: AccessResourcePermissions): AccessFeatureFilters => {
  return UI_FEATURE_RESTRICTION_OPTIONS.reduce((featureFilters, featureRestriction) => {
    featureFilters[featureRestriction.resource] = accessResourcePermissions[featureRestriction.resource]?.access_filter;
    return featureFilters;
  }, {});
};
